/**
 * Bring native x/y into 0…1 for canvas (top-left origin, y down).
 */

/** Valid normalized screen fraction (inclusive 0 and 1). */
export function isNormalizedUnit01(v: number): boolean {
  return Number.isFinite(v) && v >= 0 && v <= 1;
}

/** Half-fruit coords may sit slightly off-screen (same as phone). */
export function isRenderableRemoteNormCoord(v: number): boolean {
  return Number.isFinite(v) && v >= -1 && v <= 2;
}

/**
 * Values from native JSON for x/y/x1/y2…: 0…1, optional extended −1…2 off-screen, or 2…100 as percent.
 * (1, 1.1] — tiny overflow clamp to 1 (avoids 1.1/100 misread as percent).
 * (1.1, 2] — extended normalized (off-screen), not percent.
 * (2, 100] — percent (50 → 0.5).
 * [-1, 0) — off-screen left/top.
 * Returns null if outside supported ranges.
 */
export function normalizeCoordFromNativeJson(v: number): number | null {
  if (!Number.isFinite(v)) return null;
  if (v >= 0 && v <= 1) return v;
  if (v < 0 && v >= -1) return v;
  if (v > 1 && v <= 1.1) return Math.min(1, v);
  if (v > 1.1 && v <= 2) return v;
  if (v > 2 && v <= 100) return v / 100;
  return null;
}

export interface NormAxisRefs {
  readonly refWidth?: number;
  readonly refHeight?: number;
}

export interface NormFlipFlags {
  readonly flipX?: boolean;
  readonly flipY?: boolean;
}

export function toUnitScalar(value: number, ref?: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value >= 0 && value <= 1) return value;
  // Pixel coords: no clamp to 0…1 so objects can leave the view (same as phone).
  if (ref !== undefined && ref > 0 && (value > 1 || value < 0)) {
    return value / ref;
  }
  if (value < 0 && value >= -1) return value;
  if (value > 1 && value <= 1.1) return Math.min(1, value);
  if (value > 1.1 && value <= 2) return value;
  if (value > 2 && value <= 100) return value / 100;
  return Math.max(0, Math.min(1, value));
}

/**
 * Pose / WebRTC keypoints: visible game axes are **0…1**; anything else is off-screen and must
 * **not** be remapped (no “extended” −1…2 bands, no `(2, 100]` as percent — e.g. `y ≈ 3.28` stays
 * `3.28` so the renderer hides it). Only `normRef*` turns pixel buffer values into fractions.
 */
export function toUnitScalarPose(value: number, ref?: number): number {
  if (!Number.isFinite(value)) return 0;
  if (ref !== undefined && ref > 0 && (value > 1 || value < 0)) {
    return value / ref;
  }
  return value;
}

export function toNormXY(
  x: number,
  y: number,
  refs: NormAxisRefs,
): { nx: number; ny: number } {
  return {
    nx: toUnitScalar(x, refs.refWidth),
    ny: toUnitScalar(y, refs.refHeight),
  };
}

export function applyNormFlip(
  nx: number,
  ny: number,
  flips: NormFlipFlags,
): { nx: number; ny: number } {
  return {
    nx: flips.flipX ? 1 - nx : nx,
    ny: flips.flipY ? 1 - ny : ny,
  };
}

/**
 * Axes in {@link normalizePose}: finite check only. Do not reinterpret coordinates here — the
 * playfield is **0…1** per axis; TV clips/hides outside that in the canvas layer.
 */
export function normalizeNativeAxisForPose(v: number): number {
  return Number.isFinite(v) ? v : 0;
}

/**
 * Target radius: fraction of logical height (0…1), or whole percent (e.g. 10 → 0.1).
 */
export function normalizeRadiusFromNativeJson(v: number): number | null {
  if (!Number.isFinite(v) || v < 0) return null;
  if (v <= 1) return v;
  if (v > 1 && v <= 100) return v / 100;
  return null;
}
